import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2026-02-19 12:26:35 +0100 (Thu, 19 Feb 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 160857 $
//  Purpose   : upload via webdav to help.vls.io
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Form, Button, Message, Input, Icon, List } from 'semantic-ui-react';
import now from 'performance-now';
// ---Framework imports
import log from 'uix/logging';
import { Actions } from 'uix/registries';
import { usePersistent, useState, useActions, useSelector, useApi, useNotify } from 'uix/hooks';
import { HeaderSegment, XMenu } from 'uix/components';
import { prettyBytes } from 'uix/utils';
import { formatDate } from 'uix/utils/date';
import { Differ, mimetype2options } from 'uix/components/editor';
// ---Local imports
import { MIMETYPES, MIME } from './utils';
// ---Globals
const FileDetails = ({ name, size, author, modified, url })=>{
    return /*#__PURE__*/ _jsxs(List, {
        horizontal: true,
        relaxed: true,
        children: [
            /*#__PURE__*/ _jsxs(List.Item, {
                children: [
                    /*#__PURE__*/ _jsx(List.Header, {
                        children: "File"
                    }),
                    url ? /*#__PURE__*/ _jsx("a", {
                        href: url,
                        target: "file_details",
                        style: {
                            color: size === undefined ? '#A44' : undefined
                        },
                        children: url
                    }) : name
                ]
            }),
            author && /*#__PURE__*/ _jsxs(List.Item, {
                children: [
                    /*#__PURE__*/ _jsx(List.Header, {
                        children: "Autor"
                    }),
                    author
                ]
            }),
            modified && /*#__PURE__*/ _jsxs(List.Item, {
                children: [
                    /*#__PURE__*/ _jsx(List.Header, {
                        children: "Änderungsdatum"
                    }),
                    formatDate(modified)
                ]
            }),
            size !== undefined && /*#__PURE__*/ _jsxs(List.Item, {
                children: [
                    /*#__PURE__*/ _jsx(List.Header, {
                        children: "Größe"
                    }),
                    prettyBytes(size)
                ]
            })
        ]
    });
};
const WikiHubFileInfo = ({ identifier, mimetype, changeTs })=>{
    let { data, error } = useApi('getWikiHubInfo', {
        identifier,
        mimetype
    }, {
        deps: changeTs,
        autoreset: false
    });
    if (!data) {
        return null;
    }
    return /*#__PURE__*/ _jsx(FileDetails, {
        name: data.filename,
        modified: data.version?.ts,
        author: data.version?.username,
        size: data.version?.size,
        url: data.url
    });
};
const DiffUpload = ({ saveText, identifier, mimetype })=>{
    let { mode, options } = mimetype2options(mimetype);
    let { data, error } = useApi('getWikiHubInfo', {
        identifier,
        mimetype,
        loadtext: true
    });
    let leftDetails = data ? /*#__PURE__*/ _jsx(FileDetails, {
        name: "on help.vls.io",
        modified: data.version?.ts,
        author: data.version?.username
    }) : null;
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        header: "Review Changes",
        icon: "exchange",
        flex: true,
        style: {
            height: '70vh'
        },
        portal: "ReviewPortal",
        children: /*#__PURE__*/ _jsx(Differ, {
            flex: true,
            headLeft: leftDetails,
            textLeft: data?.text,
            textMiddle: saveText,
            mode: mode.name,
            xml: options.xml,
            menuPortal: "ReviewPortal"
        })
    });
};
export const WebdavUpload = ({ text, identifier, mimetype })=>{
    let notify = useNotify();
    let uploadWikiText = useActions(Actions.uploadWikiText);
    let callApi = useActions('callApi');
    let [infoActive, setInfoActive] = usePersistent('infoActive', false);
    let [username, setUsername] = usePersistent('webdavUser', '');
    let [changeTs, setChangeTs] = useState(null);
    let [password, setPassword] = useState('');
    let subheader = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            "Upload to",
            ' ',
            /*#__PURE__*/ _jsx("a", {
                href: "https://help.vls.io",
                target: "wiki_hub",
                children: "help.vls.io"
            })
        ]
    });
    let runUpload = ()=>{
        if (!password) return;
        let uploadDone = (data, error)=>{
            if (data) {
                if (data.success) {
                    notify.success({
                        header: 'Upload done',
                        message: 'successfully saved to https://help.vls.io'
                    });
                } else if (data.status_code == 401) {
                    notify.warning({
                        header: 'Authentification failed',
                        dismissAfter: 5000,
                        message: 'Either username or password is incorrect.'
                    });
                } else {
                    notify.warning({
                        header: 'Upload failed',
                        message: `code: ${data.status_code}`
                    });
                }
                setChangeTs(now());
            }
            if (error) {
                notify.error({
                    header: 'Upload error',
                    error
                });
            }
        };
        let params = {
            text,
            identifier,
            username,
            password,
            mimetype
        };
        callApi('uploadWikiToHub', params, uploadDone, {
            method: 'post'
        });
    };
    let confirmCodeDiff = ()=>/*#__PURE__*/ _jsx(DiffUpload, {
            saveText: text,
            identifier: identifier,
            mimetype: mimetype
        });
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        borderless: true,
        children: [
            /*#__PURE__*/ _jsxs(XMenu.Item, {
                children: [
                    /*#__PURE__*/ _jsx(Icon, {
                        name: "user"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        size: "mini",
                        value: username,
                        onChange: (event, data)=>setUsername(data.value),
                        onKeyDown: (event)=>event.key == 'Enter' ? runUpload() : null,
                        placeholder: "User",
                        style: {
                            width: '8em'
                        }
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(XMenu.Item, {
                children: [
                    /*#__PURE__*/ _jsx(Icon, {
                        name: "key"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        size: "mini",
                        value: password,
                        onChange: (event, data)=>setPassword(data.value),
                        onKeyDown: (event)=>event.key == 'Enter' ? runUpload() : null,
                        placeholder: "Password",
                        type: "password",
                        style: {
                            width: '8em'
                        },
                        children: /*#__PURE__*/ _jsx("input", {
                            autoComplete: "off"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "save",
                disabled: !password,
                title: "Upload",
                onClick: runUpload,
                confirm: {
                    content: confirmCodeDiff,
                    size: 'large',
                    confirmButton: 'Upload'
                }
            }),
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "info",
                active: infoActive,
                onClick: ()=>setInfoActive(!infoActive)
            })
        ]
    });
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        icon: "upload",
        header: "WebDAV",
        subheader: subheader,
        menu: menu,
        children: infoActive && /*#__PURE__*/ _jsx(WikiHubFileInfo, {
            identifier: identifier,
            mimetype: mimetype,
            changeTs: changeTs
        })
    });
};
